import { useEffect, useState } from 'react'
import { Check, Copy, MoreHorizontal, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-provider'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { AuditLogDrawer } from '@/components/collaboration/audit-log-drawer'
import { CollaboratorsDrawer } from '@/components/collaboration/collaborators-drawer'
import { CatalogDrawer } from '@/components/projects/catalog-drawer'
import { ProjectDetailFavoriteSlot } from '@/components/favorites/project-detail-favorite-slot'
import { HandleChip } from '@/components/users/handle-chip'
import { ProjectHeroBlock } from '@/components/projects/project-hero-block'
import { ProjectHeroDisplay } from '@/components/projects/project-hero-display'
import { PublishButton } from '@/components/projects/publish-button'
import { PublishedVersionBadge } from '@/components/projects/published-version-badge'
import { SlugField } from '@/components/projects/slug-field'
import { VisibilityBadge } from '@/components/projects/visibility-badge'
import { VisibilitySelector } from '@/components/projects/visibility-selector'
import { useDeleteProject, useUpdateProject } from '@/features/projects/use-projects'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import type { ProjectDetail, Visibility } from '@/api/types'

interface ProjectHeaderProps {
  project: ProjectDetail
  readOnly: boolean
}

const ShareButton = () => {
  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      toast.success('Link copied to clipboard')
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('Could not copy link')
    }
  }
  return (
    <Button variant="outline" size="sm" onClick={handleCopy} className="gap-2">
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {copied ? 'Copied' : 'Share'}
    </Button>
  )
}

const ReadOnlyHeader = ({ project }: { project: ProjectDetail }) => (
  <div className="space-y-3">
    <ProjectHeroDisplay
      displayUrl={project.heroDisplayUrl ?? null}
      alt={`${project.title} hero image`}
    />
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-3xl font-bold tracking-tight">{project.title}</h1>
        <PublishedVersionBadge
          version={project.publishedVersion ?? null}
          publishedAt={project.publishedAt ?? null}
        />
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <ProjectDetailFavoriteSlot projectId={project.id} />
        <VisibilityBadge visibility={project.visibility} />
        <ShareButton />
      </div>
    </div>
    {project.description ? (
      <p className="whitespace-pre-line text-muted-foreground">{project.description}</p>
    ) : (
      <p className="text-sm italic text-muted-foreground">No description.</p>
    )}
    <p className="text-xs text-muted-foreground">
      By <HandleChip author={project.author} />
    </p>
  </div>
)

const EditableHeader = ({ project }: { project: ProjectDetail }) => {
  const navigate = useNavigate()
  const update = useUpdateProject(project.id)
  const remove = useDeleteProject()
  const confirm = useConfirm()

  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState(project.title)
  const [descDraft, setDescDraft] = useState(project.description ?? '')

  useEffect(() => {
    if (!editingTitle) setTitleDraft(project.title)
  }, [project.title, editingTitle])

  useEffect(() => {
    setDescDraft(project.description ?? '')
  }, [project.description])

  const saveTitle = () => {
    setEditingTitle(false)
    const next = titleDraft.trim()
    if (!next) {
      setTitleDraft(project.title)
      return
    }
    if (next !== project.title) {
      update.mutate({ title: next }, { onError: () => toast.error('Failed to save title') })
    }
  }

  const saveDescription = () => {
    if (descDraft !== (project.description ?? '')) {
      update.mutate(
        { description: descDraft },
        { onError: () => toast.error('Failed to save description') }
      )
    }
  }

  const setVisibility = (next: Visibility) => {
    if (next === project.visibility) return
    update.mutate(
      { visibility: next },
      { onError: () => toast.error('Failed to update visibility') }
    )
  }

  const saveSlug = (next: string) => {
    if (next === project.slug) return
    update.mutate(
      { slug: next },
      {
        onSuccess: () => {
          toast.success('Slug updated')
          // router.replace to the new canonical URL so the address bar tracks the rename.
          navigate(canonicalProjectUrl(project.author.handle, next), { replace: true })
        },
        onError: (err) => {
          const apiErr = err as { status?: number; detail?: string }
          if (apiErr.status === 409) {
            toast.error(apiErr.detail ?? 'That slug was just taken — try another')
          } else {
            toast.error(apiErr.detail ?? 'Failed to save slug')
          }
        },
      }
    )
  }

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Delete "${project.title}"?`,
      description:
        "This removes the project, every step, and the project's catalog. This cannot be undone.",
      confirmText: 'Delete project',
      destructive: true,
    })
    if (!ok) return
    remove.mutate(project.id, {
      onSuccess: () => {
        toast.success('Project deleted')
        navigate('/dashboard')
      },
      onError: () => toast.error('Failed to delete project'),
    })
  }

  return (
    <div className="space-y-3">
      <ProjectHeroDisplay
        displayUrl={project.heroDisplayUrl ?? null}
        alt={`${project.title} hero image`}
      />
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {editingTitle ? (
            <Input
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  saveTitle()
                } else if (e.key === 'Escape') {
                  setTitleDraft(project.title)
                  setEditingTitle(false)
                }
              }}
              className="text-3xl font-bold tracking-tight"
            />
          ) : (
            <div className="flex flex-wrap items-baseline gap-3">
              <button
                type="button"
                onClick={() => setEditingTitle(true)}
                className="text-left text-3xl font-bold tracking-tight hover:underline"
                aria-label="Edit project title"
              >
                {project.title}
              </button>
              <PublishedVersionBadge
                version={project.publishedVersion ?? null}
                publishedAt={project.publishedAt ?? null}
              />
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ProjectDetailFavoriteSlot projectId={project.id} />
          <CatalogDrawer projectId={project.id} catalog={project.catalog} />
          {project.effectiveRole === 'OWNER' && <CollaboratorsDrawer projectId={project.id} />}
          {(project.effectiveRole === 'OWNER' || project.effectiveRole === 'EDITOR') && (
            <AuditLogDrawer projectId={project.id} />
          )}
          <PublishButton project={project} />
          <ShareButton />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="More actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={handleDelete}
              >
                <Trash2 className="h-4 w-4" />
                Delete project
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Textarea
        value={descDraft}
        onChange={(e) => setDescDraft(e.target.value)}
        onBlur={saveDescription}
        placeholder="Describe this project (optional)"
        rows={2}
        className="text-sm"
      />

      <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
        <p className="text-xs text-muted-foreground">
          By <HandleChip author={project.author} />
        </p>
        <VisibilitySelector
          value={project.visibility}
          onChange={setVisibility}
          name={`vis-${project.id}`}
          disabled={update.isPending}
          hideLegend
        />
        <div className="min-w-[260px] flex-1">
          <SlugField
            currentSlug={project.slug}
            excludeProjectId={project.id}
            onCommit={saveSlug}
            disabled={update.isPending}
          />
        </div>
      </div>

      <ProjectHeroBlock projectId={project.id} heroThumbUrl={project.heroThumbUrl ?? null} />
    </div>
  )
}

export const ProjectHeader = ({ project, readOnly }: ProjectHeaderProps) =>
  readOnly ? <ReadOnlyHeader project={project} /> : <EditableHeader project={project} />
